import React, { useCallback } from 'react';
import { Text, Card, Stack, Image, Box, Group, useMantineTheme, useComputedColorScheme } from '@mantine/core';
import { MenuItem as MenuItemType } from '../../types/menu';
import { t } from '../../utils/uiTranslations';

export interface MenuItemProps {
  item: MenuItemType;
  getDisplayText: (item: MenuItemType, field: 'name' | 'description' | 'disclaimer') => string;
  onItemSelect: (itemId: string) => void;
  currentLanguage?: string;
  /** When set (e.g. from AI recommendation link text), show this as the card title instead of menu item name. Keeps displayed recommendations aligned with what the AI said. */
  displayNameOverride?: string;
}

export const MenuItem: React.FC<MenuItemProps> = React.memo(({ item, getDisplayText, onItemSelect, currentLanguage = 'en', displayNameOverride }) => {
  const theme = useMantineTheme();
  const colorScheme = useComputedColorScheme('light', { getInitialValueInEffect: true });
  const isDarkMode = colorScheme === 'dark';
  // Use AI link text when provided (chat recommendations); otherwise use menu display name
  const itemName = (displayNameOverride && displayNameOverride.trim()) ? displayNameOverride.trim() : getDisplayText(item, 'name');
  const originalJapaneseName = item.name; // Original Japanese text from raw extracted data
  const priceYen = Math.floor(Number(item.price));
  const showPrice = priceYen > 0;
  const nameId = `menu-item-name-${item.id}`;
  const priceId = `menu-item-price-${item.id}`;
  
  // Show original Japanese name as subtitle when:
  // 1. Not currently viewing in Japanese (currentLanguage !== 'jp')
  // 2. Original name exists and is different from displayed name
  // 3. Not using displayNameOverride (AI text) so we don't duplicate
  const showJapaneseSubtitle = !displayNameOverride && currentLanguage !== 'jp' && 
                                originalJapaneseName && 
                                originalJapaneseName.trim() !== '' && 
                                originalJapaneseName !== itemName;
  
  // Get image size from theme layout constants
  const imageSize = theme.other?.layout?.menuItemImageSize ?? 60;

  const handleItemClick = useCallback((e: React.MouseEvent) => {
    console.log('=== MENU ITEM CLICKED ===');
    console.log('Full menu item object:', JSON.stringify(item, null, 2));
    console.log('Menu item ID:', item.id);
    console.log('Menu item name:', itemName);
    console.log('Menu item price:', item.price);
    console.log('Menu item image URL:', item.imageUrl);
    console.log('Menu item props:', item.props);
    console.log('========================');
    onItemSelect(item.id);
  }, [item, itemName, onItemSelect]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onItemSelect(item.id);
    }
  }, [item.id, onItemSelect]);

  return (
    <Card
      withBorder
      p="sm"
      id={`menu-item-${item.id}`}
      onClick={handleItemClick}
      role="button"
      tabIndex={0}
      aria-labelledby={nameId}
      aria-describedby={showPrice ? `${priceId}` : undefined}
      aria-label={showPrice
        ? t('viewDetailsAriaWithPrice', currentLanguage, { name: itemName, priceYen })
        : t('viewDetailsAriaNoPrice', currentLanguage, { name: itemName })}
      onKeyDown={handleKeyDown}
      data-interactive
      style={{ 
        height: '100%', 
        cursor: 'pointer',
        // Conditional shadow for dark mode - white glow for better visibility
        boxShadow: isDarkMode 
          ? '0 1px 3px rgba(255, 255, 255, 0.1), 0 1px 2px rgba(0, 0, 0, 0.2)' // White glow + dark shadow
          : undefined // Use Mantine's default shadow in light mode
      }}
      shadow={isDarkMode ? undefined : "sm"} // Only use Mantine shadow in light mode
      radius="md"
    >
      {/* Horizontal layout: Image on left (if exists), then name and price on the right */}
      <Group gap="sm" align="flex-start" wrap="nowrap">
        {/* Image section - Left aligned, only display if imageUrl exists */}
        {/* Uses theme layout constants for image size and theme radius for border radius */}
        {item.imageUrl && (
          <Box style={{ 
            height: `${imageSize}px`, 
            width: `${imageSize}px`, 
            minWidth: `${imageSize}px`,
            overflow: 'hidden', 
            borderRadius: theme.radius.sm,
            flexShrink: 0
          }}>
            <Image
              src={item.imageUrl}
              alt={`${itemName} image`}
              height={imageSize}
              width={imageSize}
              fit="contain"
              fallbackSrc={`https://via.placeholder.com/${imageSize}x${imageSize}/e9ecef/6c757d?text=No+Image`}
            />
          </Box>
        )}

        {/* Name and Price on the same line - name can wrap */}
        <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
          <Group justify="space-between" align="baseline" gap="xs" wrap="nowrap">
            <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
              <Text 
                id={nameId} 
                fw={600} 
                size="sm" 
                style={{ 
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word'
                }}
              >
                {itemName}
              </Text>
              {/* Show original Japanese name as subtitle when viewing in English */}
              {showJapaneseSubtitle && (
                <Text 
                  size="xs" 
                  c="dimmed" 
                  style={{ 
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word'
                  }}
                >
                  {originalJapaneseName}
                </Text>
              )}
            </Stack>
            {showPrice && (
              <Text 
                id={priceId} 
                fw={600} 
                size="sm" 
                c="orange" 
                aria-label={t('priceAria', currentLanguage, { priceYen })}
                style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
              >
                {priceYen} 円
              </Text>
            )}
          </Group>
        </Stack>
      </Group>
    </Card>
  );
}); 